import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '@/lib/customSupabaseClient';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, UserX, User, Activity, Calendar, Users, Dumbbell, Clock, CheckCircle, Clock4, ChevronDown, ChevronUp, ChevronRight, Star, HeartPulse, ShieldAlert, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SendEmailButton } from '@/components/email/SendEmailButton';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const StatsSkeleton = () => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    {[...Array(4)].map((_, i) => (
      <Card key={i}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4 rounded" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-7 w-16" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const FeedSkeleton = () => (
  <div className="space-y-4">
    {[...Array(3)].map((_, i) => (
      <Card key={i}>
        <div className="p-4">
          <Skeleton className="h-5 w-48 mb-3" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {[...Array(3)].map((_, j) => (
              <Card key={j}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </Card>
    ))}
  </div>
);

const NoResults = () => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center h-64 gap-2">
      <UserX className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm font-medium text-muted-foreground">No Users Found</p>
      <p className="text-xs text-muted-foreground">Try adjusting your search term.</p>
    </CardContent>
  </Card>
);

const UserCard = ({ user, activationUsers }) => {
  const navigate = useNavigate();

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffSeconds = Math.round((now - date) / 1000);
    const diffMinutes = Math.round(diffSeconds / 60);
    const diffHours = Math.round(diffMinutes / 60);
    const diffDays = Math.round(diffHours / 24);

    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const isFavvityUser = (userEmail, activationList) => {
    return activationList.some(activation => activation.user_email === userEmail);
  };

  const avatarColors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'
  ];
  const colorIndex = (user.full_name || 'A').charCodeAt(0) % avatarColors.length;

  return (
    <Card
      className="cursor-pointer overflow-hidden transition-colors hover:border-primary h-full flex flex-col"
      onClick={() => navigate(`/user-activity/${user.user_id}`)}
    >
      <CardContent className="p-6 flex flex-col flex-grow">
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0 ${avatarColors[colorIndex]}`}>
            <User className="h-7 w-7"/>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold truncate">{user.full_name || 'N/A'}</p>
            <div className="flex items-center gap-1">
              <p className="text-sm text-muted-foreground truncate">{user.email}</p>
              <SendEmailButton userId={user.user_id} fullName={user.full_name} email={user.email} />
            </div>
            {isFavvityUser(user.email, activationUsers) && (
              <Badge variant="default" className="mt-2">Favvity User</Badge>
            )}
          </div>
        </div>
        <div className="mt-6 space-y-3 text-sm flex-grow">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Activity className="h-4 w-4" />
            <Badge variant="secondary">{user.total_sessions || 0} sessions</Badge>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Dumbbell className="h-4 w-4" />
            <Badge variant="secondary">{user.total_exercises || 0} exercises</Badge>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-dashed">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            <span>Last activity: {formatRelativeTime(user.last_activity_at)}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
            <Clock className="h-3.5 w-3.5" />
            <span>Last login: {user.last_sign_in_at ? formatRelativeTime(user.last_sign_in_at) : 'Never'}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ActivityCard = ({ session, isExpanded, onToggleExpand, exercises, isLoadingExercises }) => {
  const navigate = useNavigate();
  const avatarColors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'
  ];
  const colorIndex = (session.user_name || 'A').charCodeAt(0) % avatarColors.length;

  const getStatusBadge = (status) => {
    switch(status) {
      case 'completed': return <Badge variant="success"><CheckCircle className="mr-1 h-3 w-3" />Completed</Badge>;
      case 'abandoned': return null;
      case 'in_progress': return <Badge variant="default"><Clock4 className="mr-1 h-3 w-3" />In Progress</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const groupedExercises = useMemo(() => {
    if (!exercises) return {};
    return exercises.reduce((acc, ex) => {
      const category = ex.exercise_category || 'Uncategorized';
      if (!acc[category]) acc[category] = [];
      acc[category].push(ex);
      return acc;
    }, {});
  }, [exercises]);

  const formatSetsData = (setsData) => {
    if (!setsData || setsData.length === 0) return null;
    const firstSet = setsData[0];
    const numSets = setsData.length;
    const reps = firstSet.reps;
    const weight = firstSet.weight;
    const unit = firstSet.unit || 'kg';

    let result = `${numSets} x ${reps}`;
    if (weight) result += ` @ ${weight}${unit}`;
    return result;
  };

  return (
    <Card className="overflow-hidden transition-colors hover:border-primary flex flex-col h-full">
      <div className="p-4 space-y-3 flex-grow">
        <div 
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate(`/user-activity/${session.user_id}`)}
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 ${avatarColors[colorIndex]}`}>
            {session.user_name?.charAt(0) || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold truncate">{session.user_name || 'Unknown User'}</p>
            <p className="text-xs text-muted-foreground truncate">{session.program_name || 'No Program'}</p>
          </div>
        </div>
        <div 
          className="cursor-pointer"
          onClick={() => navigate(`/user-activity/${session.user_id}`)}
        >
          <p className="font-semibold text-sm truncate">{session.session_name || 'Untitled Session'}</p>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-dashed">
          <span>{getStatusBadge(session.status)}</span>
          <div className="flex items-center gap-3">
            {session.duration_seconds && <span>{Math.round(session.duration_seconds / 60)} min</span>}
            <span>{session.exercises_completed}/{session.total_exercises} exercises</span>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="bg-muted/30 border-t">
          <div className="p-4">
            {isLoadingExercises && (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-4 flex-1" />
                  </div>
                ))}
              </div>
            )}
            {!isLoadingExercises && exercises && Object.keys(groupedExercises).length > 0 && (
              <div className="space-y-4">
                {Object.entries(groupedExercises).map(([category, exs]) => (
                  <div key={category}>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 uppercase">{category}</h4>
                    <div className="space-y-3">
                      {exs.map((ex, index) => (
                        <div key={ex.exercise_id || index} className="text-sm p-2 bg-background rounded-md">
                          <div className="flex justify-between items-start">
                            <p className="font-medium">
                              <span className="font-bold mr-2">{String.fromCharCode(65 + (ex.exercise_order - 1 || index))}.</span>
                              {ex.exercise_name}
                            </p>
                            {ex.completed_at && <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />}
                          </div>
                          <div className="pl-6 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                            {ex.sets_data && <span className="font-mono text-primary">{formatSetsData(ex.sets_data)}</span>}
                            {ex.rpe_reported && <Badge variant="outline" className="flex items-center"><HeartPulse className="h-3 w-3 mr-1" /> RPE: {ex.rpe_reported}</Badge>}
                            {ex.pain_level_reported > 0 && <Badge variant="destructive" className="flex items-center"><ShieldAlert className="h-3 w-3 mr-1" /> Pain: {ex.pain_level_reported}</Badge>}
                            {ex.form_rating && <Badge variant="secondary" className="flex items-center"><Star className="h-3 w-3 mr-1" /> Form: {ex.form_rating}/5</Badge>}
                          </div>
                          {ex.exercise_notes && <p className="pl-6 mt-2 text-xs italic text-muted-foreground">"{ex.exercise_notes}"</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!isLoadingExercises && (!exercises || exercises.length === 0) && (
              <p className="text-center text-sm text-muted-foreground p-4">No exercise details available for this session.</p>
            )}
          </div>
        </div>
      )}

      <div className="border-t">
        <Button variant="ghost" className="w-full justify-center text-xs text-muted-foreground" onClick={onToggleExpand}>
          {isExpanded ? "See Less" : "See More"}
          {isExpanded ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </Card>
  );
};

const UserActivityDashboard = () => {
  const { isAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7');
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [errorStats, setErrorStats] = useState(null);
  const [expandAllCards, setExpandAllCards] = useState(false);
  const [expandedDates, setExpandedDates] = useState(() => {
    const today = new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    return new Set([today]);
  });
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [activityFeed, setActivityFeed] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [errorActivity, setErrorActivity] = useState(null);
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [sessionExercises, setSessionExercises] = useState(new Map());
  const [loadingExercises, setLoadingExercises] = useState(new Set());
  const [activationUsers, setActivationUsers] = useState([]);

  const fetchSessionExercises = useCallback(async (sessionId) => {
    if (sessionExercises.has(sessionId) || loadingExercises.has(sessionId)) return;

    setLoadingExercises(prev => new Set(prev.add(sessionId)));

    try {
      const { data, error } = await supabase.rpc('admin_get_training_session_details', {
        p_training_session_id: sessionId
      });
      if (error) throw error;
      setSessionExercises(prev => new Map(prev.set(sessionId, data || [])));
    } catch (err) {
      console.error(`Error fetching exercises for session ${sessionId}:`, err);
    } finally {
      setLoadingExercises(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  }, [sessionExercises, loadingExercises]);
  
  const toggleSessionExpand = (sessionId) => {
    const newSet = new Set(expandedSessions);
    if (newSet.has(sessionId)) {
      newSet.delete(sessionId);
    } else {
      newSet.add(sessionId);
      fetchSessionExercises(sessionId);
    }
    setExpandedSessions(newSet);
  };

  const toggleDateExpand = (date) => {
    const newSet = new Set(expandedDates);
    if (newSet.has(date)) {
      newSet.delete(date);
    } else {
      newSet.add(date);
    }
    setExpandedDates(newSet);
  };

  const fetchActivityFeed = useCallback(async () => {
    setLoadingActivity(true);
    setErrorActivity(null);
    try {
      const { data, error } = await supabase.rpc('admin_get_all_activity_feed', {
        p_limit: 500,
        p_offset: 0,
        p_days_back: parseInt(dateRange),
        p_user_id: null,
        p_search_term: null
      });
      if (error) throw error;
      setActivityFeed(data || []);
    } catch (err) {
      console.error('Error fetching activity feed:', err);
      setErrorActivity('Failed to load activity feed.');
    } finally {
      setLoadingActivity(false);
    }
  }, [dateRange]);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      setLoadingStats(true);
      setErrorStats(null);
      try {
        const { data, error } = await supabase.rpc('admin_get_dashboard_stats', { p_days_back: parseInt(dateRange) });
        if (error) throw error;
        if (data && data.length > 0) setStats(data[0]);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setErrorStats('Failed to load dashboard statistics.');
      } finally {
        setLoadingStats(false);
      }
    };

    const fetchActivationData = async () => {
      try {
        const { data, error } = await supabase
          .from('activation_dashboard')
          .select('user_email');
        if (error) throw error;
        setActivationUsers(data || []);
      } catch (err) {
        console.error('Error fetching activation data:', err);
      }
    };

    fetchDashboardStats();
    if (isAdmin) fetchActivationData();
    if (!searchTerm.trim()) fetchActivityFeed();
  }, [dateRange, searchTerm, fetchActivityFeed, isAdmin]);

  const fetchUsers = useCallback(async (searchValue) => {
    if (!searchValue.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc('search_users_with_activity', {
        p_search_term: searchValue,
        p_limit: 20,
      });
      if (rpcError) throw rpcError;
      setResults(data || []);
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to fetch user activity. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (expandAllCards) {
      activityFeed.forEach(session => {
        if (!expandedSessions.has(session.training_session_id)) {
          fetchSessionExercises(session.training_session_id);
        }
      });
    }
  }, [expandAllCards, activityFeed, fetchSessionExercises, expandedSessions]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchUsers(searchTerm);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm, fetchUsers]);

  const groupedByDate = useMemo(() => {
    if (!activityFeed || activityFeed.length === 0) return new Map();

    let filteredFeed = activityFeed.filter(session => session.status !== 'abandoned');

    if (userTypeFilter !== 'all') {
      filteredFeed = filteredFeed.filter(session => {
        const isB2B = activationUsers.some(activation => activation.user_email === session.user_email);
        if (userTypeFilter === 'b2b') return isB2B;
        if (userTypeFilter === 'my_athletes') return !isB2B;
        return true;
      });
    }

    const grouped = filteredFeed.reduce((acc, session) => {
      const date = new Date(session.started_at).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      if (!acc[date]) acc[date] = [];
      acc[date].push(session);
      return acc;
    }, {});

    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));
    const sortedMap = new Map();
    sortedDates.forEach(date => sortedMap.set(date, grouped[date]));
    return sortedMap;
  }, [activityFeed, userTypeFilter, activationUsers]);
  
  const renderContent = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-14 w-14 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                </div>
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-28" />
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }
    if (error) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 gap-2">
            <p className="text-sm font-medium text-destructive">{error}</p>
          </CardContent>
        </Card>
      );
    }
    if (!searchTerm.trim()) {
      if (loadingActivity) return <FeedSkeleton />;
      if (errorActivity) return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 gap-2">
            <p className="text-sm font-medium text-destructive">{errorActivity}</p>
          </CardContent>
        </Card>
      );
      if (groupedByDate.size === 0) return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 gap-2">
            <Activity className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium text-muted-foreground">No Recent Activity</p>
            <p className="text-xs text-muted-foreground">No user activities found for the selected date range and filter.</p>
          </CardContent>
        </Card>
      );
      return (
        <div className="space-y-4">
          {Array.from(groupedByDate.entries()).map(([date, sessions]) => {
            const isDateExpanded = expandedDates.has(date);
            return (
              <Card key={date} className="overflow-hidden">
                <div 
                  onClick={() => toggleDateExpand(date)}
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors select-none"
                >
                  <div className="flex items-center gap-3">
                    <div className={`transition-transform duration-200 ${isDateExpanded ? 'rotate-90' : ''}`}>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <h2 className="text-sm font-medium text-muted-foreground">{date}</h2>
                    <Badge variant="secondary">
                      {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
                    </Badge>
                  </div>
                </div>
                {isDateExpanded && (
                  <div className="p-4 pt-0 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
                      {sessions.map(session => {
                        const sessionId = session.training_session_id;
                        const isExpanded = expandAllCards || expandedSessions.has(sessionId);
                        return (
                          <ActivityCard 
                            key={sessionId} 
                            session={session} 
                            isExpanded={isExpanded}
                            onToggleExpand={() => toggleSessionExpand(sessionId)}
                            exercises={sessionExercises.get(sessionId)}
                            isLoadingExercises={loadingExercises.has(sessionId)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      );
    }
    if (results.length === 0) return <NoResults />;
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {results.map((user) => (
          <UserCard key={user.user_id} user={user} activationUsers={activationUsers} />
        ))}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>User Activity - My Movement Space</title>
      </Helmet>

      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">User Activity Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Monitor user activity and search for users.</p>
      </div>
      
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Last {dateRange} Days Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStats && <StatsSkeleton />}
            {errorStats && !loadingStats && (
              <div className="flex items-center justify-center h-24 text-sm text-destructive">{errorStats}</div>
            )}
            {stats && !loadingStats && !errorStats && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.total_sessions || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.active_users || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Exercises</CardTitle>
                    <Dumbbell className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.total_exercises_completed || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Avg Session Duration</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {stats.avg_session_duration_min || 0}
                      <span className="text-sm font-normal text-muted-foreground"> min</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Search Users</CardTitle>
            <CardDescription>Find users by their name or email address. Clear search to see the latest activity feed.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-center md:gap-4 space-y-4 md:space-y-0">
              <div className="relative flex-grow md:w-[70%]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search by name or email..."
                  className="w-full pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="md:w-[30%]">
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 Days</SelectItem>
                    <SelectItem value="30">Last 30 Days</SelectItem>
                    <SelectItem value="90">Last 90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {!searchTerm.trim() && (
          <div className="flex items-center justify-between py-2 px-3 rounded-lg border bg-card">
            <Label htmlFor="expand-all-cards" className="text-sm font-medium flex items-center gap-2">
              Expand all cards
            </Label>
            <Switch
              id="expand-all-cards"
              checked={expandAllCards}
              onCheckedChange={setExpandAllCards}
            />
          </div>
        )}

        {isAdmin && !searchTerm.trim() && (
          <div className="flex flex-wrap gap-2">
            <Button 
              variant={userTypeFilter === 'all' ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setUserTypeFilter('all')}
              className="gap-2"
            >
              <Users className="h-4 w-4" />
              All Users
            </Button>
            <Button 
              variant={userTypeFilter === 'my_athletes' ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setUserTypeFilter('my_athletes')}
              className="gap-2"
            >
              <User className="h-4 w-4" />
              My Athletes
            </Button>
            <Button 
              variant={userTypeFilter === 'b2b' ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setUserTypeFilter('b2b')}
              className="gap-2"
            >
              <Building2 className="h-4 w-4" />
              B2B Only
            </Button>
          </div>
        )}

        <div>
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default UserActivityDashboard;
